import axios, { AxiosResponse } from "axios";
import { getCookie } from "@util/cookie";
import {
    TrainingActivityGranularity,
    TrainingActivitySummary,
    TrainingActivityTracking,
} from "@type/subscription";

const apiURL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;

export async function getSubscriptionActive(): Promise<any> {
    const token = getCookie("authToken");

    return await axios({
        url: `${apiURL}/subscriptions/active`,
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
    });
}

export async function sendSubscriptionInquiry(planOption: string, notes: string): Promise<any> {
    const token = getCookie("authToken");

    return await axios({
        url: `${apiURL}/subscriptions/send-inquiry`,
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        data: {
            plan_option: planOption,
            notes,
        },
    });
}

/** Optional admin/super-admin date-range filter (YYYY-MM-DD, inclusive). Only
 *  sent from the admin Hiring Activity screen; the BE applies it only for
 *  admin/super-admin roles. */
export interface SubscriptionDateRange {
    start_date: string;
    end_date: string;
}

export async function getSubscriptionActivitySummary(
    companyId?: string,
    dateRange?: SubscriptionDateRange
): Promise<any> {
    const token = getCookie("authToken");

    const params: Record<string, string> = {};
    if (companyId) params.company_id = companyId;
    if (dateRange) {
        params.start_date = dateRange.start_date;
        params.end_date = dateRange.end_date;
    }

    return await axios({
        url: `${apiURL}/subscriptions/activity-summary`,
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        params: Object.keys(params).length ? params : undefined,
    });
}

export async function getSubscriptionActivityTracking(
    type: string,
    range: string,
    companyId?: string,
    dateRange?: SubscriptionDateRange
): Promise<any> {
    const token = getCookie("authToken");

    return await axios({
        url: `${apiURL}/subscriptions/activity-tracking`,
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        params: {
            type,
            range,
            ...(companyId ? { company_id: companyId } : {}),
            ...(dateRange
                ? { start_date: dateRange.start_date, end_date: dateRange.end_date }
                : {}),
        },
    });
}

export interface SubscriptionPackageItem {
    id: string;
    package_name: string;
    /** Which platform the package belongs to ("recruiter" | "training" | "agent").
     *  Optional: absent on older BE payloads — the UI just omits the label then. */
    platform?: string;
}

export async function getSubscriptionPaidPackages(): Promise<any> {
    const token = getCookie("authToken");

    return await axios({
        url: `${apiURL}/subscriptions/packages/paid`,
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
    });
}

export interface SetupSubscriptionRequest {
    company_id: string;
    billing_name: string;
    billing_company: string;
    billing_email: string;
    billing_address: string;
    billing_unit_floor?: string;
    billing_city: string;
    billing_state_province: string;
    billing_zip_postal: string;
    billing_country: string;
    payment_currency: string;
    selected_package_id: string;
    subscription_started_at: string;
    is_subscription_paid: boolean;
    // Always sent — "" when the admin left the field empty.
    invoice_number: string;
    // Always sent — "yyyy-MM-dd", or "" when no date was picked.
    invoice_date: string;
}

export async function setupSubscription(body: SetupSubscriptionRequest): Promise<any> {
    const token = getCookie("authToken");

    return await axios({
        url: `${apiURL}/subscriptions/setup`,
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        data: body,
    });
}

export async function getCompanyApplications(
    page: number,
    limit: number,
    type: string,
    period: string,
    companyId?: string
): Promise<any> {
    const token = getCookie("authToken");

    return await axios({
        url: `${apiURL}/companies/applications`,
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        params: {
            type,
            page,
            limit,
            range: period,
            ...(companyId ? { company_id: companyId } : {}),
        },
    });
}

// ── Training Workspace Activity ──────────────────────────────────────────────
// Dedicated training-shaped activity for the Subscription → Workspace Activity
// tab (training platform). Scoped to the caller's training workspace via the
// Bearer token. Contract: ourteam-backend#3259 (live). Consumed by
// src/containers/billing-training-activity.tsx.

export async function getTrainingActivitySummary(): Promise<
    AxiosResponse<{ code: number; data: TrainingActivitySummary }>
> {
    const token = getCookie("authToken");

    return await axios({
        url: `${apiURL}/subscriptions/training/activity-summary`,
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
    });
}

export async function getTrainingActivityTracking(
    granularity: TrainingActivityGranularity,
): Promise<AxiosResponse<{ code: number; data: TrainingActivityTracking }>> {
    const token = getCookie("authToken");

    return await axios({
        url: `${apiURL}/subscriptions/training/activity-tracking`,
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        params: { granularity },
    });
}